import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { PROSE_HEADING_ELEMENT_ID, PROSE_TEXT_ELEMENT_ID, isQuoteBoundProseKind } from '@/blocks/domain/helpers/proseElements';
import { isBlankProseHtml } from '@/document-render/application/isBlankProseHtml';
import { useOptionalEditableDocument } from '@/document-render/presentation/editing/EditableDocumentContext';
import { useCanvasSelection } from '@/document-render/presentation/editing/useCanvasSelection';

/** Puts the caret in the element's editor when it starts editing (DB-05 review M1). */
function useFocusOnActivate(active: boolean, selector: string): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (active) ref.current?.querySelector<HTMLElement>(selector)?.focus();
  }, [active, selector]);
  return ref;
}

export interface ProseCanvasHeadingModel {
  isEditing: boolean;
  /** Nothing to draw: no title and not being edited. */
  hidden: boolean;
  commit: (next: string) => void;
  focusRef: RefObject<HTMLDivElement | null>;
}

/** The block-editor Heading element: editable inline only while selected on a writable canvas. */
export function useProseCanvasHeading(hasTitle: boolean): ProseCanvasHeadingModel {
  const editable = useOptionalEditableDocument();
  const { isSelected } = useCanvasSelection(PROSE_HEADING_ELEMENT_ID);
  const isEditing = isSelected && Boolean(editable?.renderProseEditor);
  const focusRef = useFocusOnActivate(isEditing, 'textarea');
  return {
    isEditing,
    hidden: !hasTitle && !isEditing,
    commit: (next) => editable?.onFieldTextChange(PROSE_HEADING_ELEMENT_ID, next),
    focusRef
  };
}

export type ProseCanvasTextContent = 'placeholder' | 'editor' | 'empty' | 'static';

export interface ProseCanvasTextModel {
  content: ProseCanvasTextContent;
  /** The mounted editor when `content === 'editor'`. */
  editor: ReactNode;
  focusRef: RefObject<HTMLDivElement | null>;
}

/**
 * The block-editor Text element (LLAMS-610 DB-05): Project scope shows its quote-builder
 * placeholder (D-15); a selected body on a writable canvas mounts the editor slot in place; an
 * empty or emptied (`<p></p>`) body offers "Add text"; otherwise the resolved body shows.
 */
export function useProseCanvasText(kind: string, bodyHtml: string, hasStaticBody: boolean): ProseCanvasTextModel {
  const editable = useOptionalEditableDocument();
  const { isSelected } = useCanvasSelection(PROSE_TEXT_ELEMENT_ID);
  const renderEditor = editable?.renderProseEditor;
  const quoteBound = isQuoteBoundProseKind(kind);
  const isEditing = !quoteBound && isSelected && Boolean(renderEditor);
  const focusRef = useFocusOnActivate(isEditing, '[contenteditable="true"]');

  let content: ProseCanvasTextContent = 'static';
  if (quoteBound) content = 'placeholder';
  else if (isEditing) content = 'editor';
  else if (!hasStaticBody || isBlankProseHtml(bodyHtml)) content = 'empty';

  const editor =
    content === 'editor' && renderEditor
      ? renderEditor({
          kind,
          html: bodyHtml,
          onCommit: (next) => editable?.onFieldTextChange(PROSE_TEXT_ELEMENT_ID, next),
          onCancel: () => editable?.selectElement(null)
        })
      : null;

  return { content, editor, focusRef };
}
